import React from 'react';

import GlossaryTermDropdown from './action/GlossaryTermsDropdown';
import DataProductsDropdown from './action/DataProductsDropdown';
import DeprecationDropdown from './action/DeprecationDropdown';
import DomainDropdown from './action/DomainsDropdown';
import OwnersDropdown from './action/OwnersDropdown';
import TagsDropdown from './action/TagsDropdown';

import { useTranslation } from '../../../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../../../useEntityRegistry';
import { ENTITY_ACTIONS } from '../../../rbacConstantsService';
import { EntityCapabilityType } from '../../../../Entity';
import { EntityAndType } from '../../../types';
import { SelectActionGroups } from './types';
import { useRbac } from '../../../useRbac';

import { EntityType } from '../../../../../../types.generated';

/**
 * The set of action groups that are visible by default.
 *
 * Currently, only the change tags action is implemented.
 */
const DEFAULT_ACTION_GROUPS = [
    SelectActionGroups.CHANGE_TAGS,
    SelectActionGroups.CHANGE_GLOSSARY_TERMS,
    SelectActionGroups.CHANGE_DOMAINS,
    SelectActionGroups.CHANGE_OWNERS,
    SelectActionGroups.CHANGE_DEPRECATION,
    SelectActionGroups.DELETE,
    SelectActionGroups.CHANGE_DATA_PRODUCTS,
];

type Props = {
    selectedEntities: EntityAndType[];
    visibleActionGroups?: Set<SelectActionGroups>;
    refetch?: () => void;
};

/**
 * A component used for rendering a group of actions to take on a group of selected entities such
 * as changing owners, tags, domains, etc.
 */
export const SearchSelectActions = ({
    selectedEntities,
    visibleActionGroups = new Set(DEFAULT_ACTION_GROUPS),
    refetch,
}: Props) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const { canPerformEntityAction, canPerformEntityMenuAction } = useRbac();
    const unauthorizedTooltip = t('UNAUTHORISED_TOOLTIP');

    const selectedEntityUrns = selectedEntities.map((entity) => entity.urn);
    const selectedEntityTypes = new Set(selectedEntities.map((entity) => entity.type));

    const isEntityCapabilitySupported = (type: EntityCapabilityType, entityTypes: Set<EntityType>) => {
        return Array.from(entityTypes).every((entityType) =>
            entityRegistry.getSupportedEntityCapabilities(entityType).has(type),
        );
    };

    const hasPrivilegeForAllTypes = (privilegeName: string): boolean => {
        return Array.from(selectedEntityTypes).every((entityType) => canPerformEntityAction(entityType, privilegeName));
    };

    const hasDeprecationPrivilegeForAllTypes = (): boolean => {
        return Array.from(selectedEntityTypes).every((entityType) =>
            canPerformEntityMenuAction(entityType, ENTITY_ACTIONS.EDIT_DEPRECATION),
        );
    };

    const isGlossaryTermsAuthorized = hasPrivilegeForAllTypes(ENTITY_ACTIONS.GLOSSARY_TERMS);
    const isDataProductsAuthorized = hasPrivilegeForAllTypes(ENTITY_ACTIONS.DATA_PRODUCTS);
    const isDomainsAuthorized = hasPrivilegeForAllTypes(ENTITY_ACTIONS.DOMAINS);
    const isOwnersAuthorized = hasPrivilegeForAllTypes(ENTITY_ACTIONS.OWNERS);
    const isTagsAuthorized = hasPrivilegeForAllTypes(ENTITY_ACTIONS.TAGS);
    const isDeprecationAuthorized = hasDeprecationPrivilegeForAllTypes();

    const isCapabilityDisabled = (type: EntityCapabilityType) =>
        selectedEntityUrns.length === 0 || !isEntityCapabilitySupported(type, selectedEntityTypes);

    const getCapabilityProps = (type: EntityCapabilityType, isAuthorized: boolean) => {
        const isSupported = !isCapabilityDisabled(type);
        return {
            disabled: !isSupported || !isAuthorized,
            tooltip: !isAuthorized && isSupported ? unauthorizedTooltip : undefined,
        };
    };

    return (
        <>
            {visibleActionGroups.has(SelectActionGroups.CHANGE_OWNERS) && (
                <OwnersDropdown
                    urns={selectedEntityUrns}
                    {...getCapabilityProps(EntityCapabilityType.OWNERS, isOwnersAuthorized)}
                    refetch={refetch}
                />
            )}
            {visibleActionGroups.has(SelectActionGroups.CHANGE_GLOSSARY_TERMS) && (
                <GlossaryTermDropdown
                    urns={selectedEntityUrns}
                    {...getCapabilityProps(EntityCapabilityType.GLOSSARY_TERMS, isGlossaryTermsAuthorized)}
                    refetch={refetch}
                />
            )}
            {visibleActionGroups.has(SelectActionGroups.CHANGE_TAGS) && (
                <TagsDropdown
                    urns={selectedEntityUrns}
                    {...getCapabilityProps(EntityCapabilityType.TAGS, isTagsAuthorized)}
                    refetch={refetch}
                />
            )}
            {visibleActionGroups.has(SelectActionGroups.CHANGE_DOMAINS) && (
                <DomainDropdown
                    urns={selectedEntityUrns}
                    {...getCapabilityProps(EntityCapabilityType.DOMAINS, isDomainsAuthorized)}
                    refetch={refetch}
                />
            )}
            {visibleActionGroups.has(SelectActionGroups.CHANGE_DEPRECATION) && (
                <DeprecationDropdown
                    urns={selectedEntityUrns}
                    {...getCapabilityProps(EntityCapabilityType.DEPRECATION, isDeprecationAuthorized)}
                    refetch={refetch}
                />
            )}
            {visibleActionGroups.has(SelectActionGroups.CHANGE_DATA_PRODUCTS) && (
                <DataProductsDropdown
                    urns={selectedEntityUrns}
                    {...getCapabilityProps(EntityCapabilityType.DATA_PRODUCTS, isDataProductsAuthorized)}
                    refetch={refetch}
                />
            )}
        </>
    );
};
